import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { getAdminList, updateAdminById } from "action/admin";
import { adminList } from "constants/admin";
import { useDispatch, useSelector } from "react-redux";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { REGEX } from "constants/CommonConstants";


function AdminListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    status: null,
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const adminListData = useSelector((state) => state.admin.adminList);

  const tableData = adminListData?.data?.User?.map((item, index) => {
    return {
      ...item,
      id: item.ID,
      srNo:
        (pageNum - 1) *
        (adminListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      fullName: item?.fullName || "-",
      email: item?.email || "-",
      mobile: item?.mobile || "-",
      status: item?.status || "-",
      registeredOn: item?.createdOn || "-",
    };
  });

  useEffect(() => {
    getAdminList(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({ type: adminList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (adminListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: adminListData?.data?.User?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (adminListData?.code || adminListData?.message) {
      setListState({
        isLoading: false,
        error:
          adminListData?.message ||
          "Failed to load admin list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [adminListData]);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "FullName",
        condition: "match",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    if (filterState.status?.value) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Name",
      accessor: "fullName",
      minWidth: 220,
      maxWidth: 300,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Email ID",
      accessor: "email",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Mobile No.",
      accessor: "mobile",
      centerAlign: true,
      minWidth: 135,
      maxWidth: 135,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 80,
      maxWidth: 80,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Updated By",
      accessor: "createdBy",
      centerAlign:true,
      minWidth: 180,
      maxWidth: 180,
      Cell: ({ value }) => <div className="text-wrapper">{value || "—"}</div>,
    },
    {
      Header: "Registered On",
      accessor: "registeredOn",
      minWidth: 185,
      maxWidth: 200,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Actions",
      minWidth: 130,
      maxWidth: 130,
      disableSortBy: true,
      Cell: ({ row }) => {
        const { id, status } = row.original;
        const isActive = status === "ACTIVE";
        const encodedId = urlEncodedId(id);

        return (
          <div className="d-flex align-items-center gap-3">
            <Button
              className="btn-link p-0"
              id={`edit${encodedId}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/admin/edit/${encodedId}`
                    : `/admin/list/edit/${encodedId}`,
                  {
                    state: { mode: "edit", adminData: row.original },
                  },
                );
              }}
            >
              <i className="fa fa-edit mr-1" />
            </Button>
            <UncontrolledTooltip target={`edit${encodedId}`}>
              Edit
            </UncontrolledTooltip>

            <span className="mr-1 text-muted">|</span>

            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${encodedId}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                  }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${encodedId}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const toggleStatus = (admin) => {
    const { status, id, fullName } = admin;

    const isActive = status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${isActive ? "inactivate" : "activate"
        } this admin?`,
      message: `Admin "${fullName}" will be marked as ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setIsUpdatingStatus(true);

          const payload = {
            status: newStatus,
          };
          const res = await updateAdminById(id, payload);
          if (res?.code === 200) {
            notify(
              "success",
              res?.message || `Status updated to ${newStatus.toLowerCase()}`,
            );
            getAdminList(dispatch, pageNum, filterState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to update status!";

            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        } finally {
          setIsUpdatingStatus(false);
        }
      },
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const clearSearch = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };
  const handleMobileChange = (e) => {
    const value = e.target.value;
    if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
      return;
    }
    setFilterState((prev) => ({
      ...prev,
      searchMobile: value,
    }));

  };

  return (
    <>
      <AdminHeader />
      {isUpdatingStatus && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Updating status... Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Admin
                </CardTitle>
                <Button
                  className="btn-deep-blue"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? `/super-admin/admin/add-new`
                        : `/admin/list/add-new`,
                      {
                        state: { mode: "add" },
                      },
                    )
                  }
                >
                  <i className="fa fa-plus mr-1 cursor" />
                  Admin
                </Button>
              </CardHeader>
              {/* Header */}

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by email"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchEmail}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by mobile"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchMobile}
                      onChange={handleMobileChange}
                      onKeyDown={handleKeyDown}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={filterState.status}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>
                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {adminListData?.data?.totalRecords > 0 ? (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {adminListData?.data?.totalFilteredRecords} of{" "}
                          {adminListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {adminListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No admins found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        adminListData?.data?.totalFilteredRecords /
                        adminListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    // rowClick={(row) => navigate(`/admin/user-details/${row.id}`)}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AdminListing;
